/**
 * Shared TOTP 2FA UI (ADR-0017 slice 5) — realm-agnostic.
 *
 * Both realms (operators via `lib/api`, portal end-users via `lib/api-portal`)
 * drive the SAME flow through a small `TotpAdapter`; only the transport differs.
 * `TotpEnroll` is the QR + confirm + recovery-codes step (reused at login for
 * forced enrollment); `TwoFactorPanel` is the account-page status/enable/disable
 * surface built on top of it.
 */
import { QRCodeSVG } from "qrcode.react";
import { Check, Copy, Loader2, ShieldCheck } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";

export interface TotpStatus {
  enabled: boolean;
  recovery_codes_remaining: number;
  /** Instance policy requires 2FA for this realm — disable is then hidden
   *  (only an operator reset clears it). */
  required?: boolean;
}
export interface TotpBegin {
  secret: string;
  otpauth_uri: string;
}
export interface TotpConfirmed {
  recovery_codes: string[];
  /** Present when confirm mints a session (forced-setup at login). */
  access_token?: string;
}

/** The transport seam — implemented over `api` (operators) or `portalApi`. */
export interface TotpAdapter {
  status(): Promise<TotpStatus>;
  begin(): Promise<TotpBegin>;
  confirm(code: string): Promise<TotpConfirmed>;
  disable(code: string): Promise<void>;
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={async () => {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-success" strokeWidth={1.5} />
      ) : (
        <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
      )}
    </Button>
  );
}

/** Shown ONCE after confirm — the one-time recovery codes the user must save. */
function RecoveryCodes({
  codes,
  onDone,
}: {
  codes: string[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <Alert tone="warning" title={t("twoFactor.recoveryTitle")}>
        {t("twoFactor.recoveryHelp")}
      </Alert>
      <div className="grid grid-cols-2 gap-2">
        {codes.map((c) => (
          <code
            key={c}
            className="select-all border border-border bg-background px-2 py-1 text-center font-mono text-sm"
          >
            {c}
          </code>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <CopyButton value={codes.join("\n")} />
        <Button type="button" onClick={onDone}>
          {t("twoFactor.recoverySaved")}
        </Button>
      </div>
    </div>
  );
}

/**
 * The enrollment step: render the QR for `begin.otpauth_uri`, take a 6-digit
 * code, confirm it, then reveal the recovery codes. `onDone` receives the
 * confirm result (so a forced-setup login can grab the minted session token).
 */
export function TotpEnroll({
  begin,
  onConfirm,
  onDone,
}: {
  begin: TotpBegin;
  onConfirm: (code: string) => Promise<TotpConfirmed>;
  onDone: (result: TotpConfirmed) => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [confirmed, setConfirmed] = React.useState<TotpConfirmed | null>(null);

  if (confirmed) {
    return (
      <RecoveryCodes codes={confirmed.recovery_codes} onDone={() => onDone(confirmed)} />
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setConfirmed(await onConfirm(code.trim()));
    } catch (err) {
      setError(
        err instanceof ApiError && err.code.includes("invalid")
          ? t("twoFactor.invalidCode")
          : err instanceof ApiError
            ? err.message
            : t("twoFactor.invalidCode"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <p className="text-sm text-muted-foreground">{t("twoFactor.scanHelp")}</p>
      <div className="flex justify-center rounded-md border border-border bg-white p-3">
        <QRCodeSVG value={begin.otpauth_uri} size={168} />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">{t("twoFactor.manualEntry")}</Label>
        <div className="flex items-center gap-2">
          <code className="block flex-1 overflow-x-auto border border-border bg-background px-2 py-1 font-mono text-xs">
            {begin.secret}
          </code>
          <CopyButton value={begin.secret} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="totp-code">{t("twoFactor.enterCode")}</Label>
        <Input
          id="totp-code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000000"
          className="font-mono tracking-widest"
          autoFocus
        />
      </div>
      {error && <Alert tone="danger">{error}</Alert>}
      <Button type="submit" disabled={busy || code.length !== 6}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />}
        {t("twoFactor.verifyEnable")}
      </Button>
    </form>
  );
}

/**
 * Account-page 2FA surface: shows status, drives enable (via `TotpEnroll`) and
 * disable (code-gated). `adapter` is the only thing that varies per realm.
 */
export function TwoFactorPanel({ adapter }: { adapter: TotpAdapter }) {
  const { t } = useTranslation();
  const [status, setStatus] = React.useState<TotpStatus | null>(null);
  const [begin, setBegin] = React.useState<TotpBegin | null>(null);
  const [disabling, setDisabling] = React.useState(false);
  const [disableCode, setDisableCode] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const reload = React.useCallback(() => {
    adapter.status().then(setStatus).catch(() => setStatus(null));
  }, [adapter]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  async function startEnroll() {
    setBusy(true);
    setError(null);
    try {
      setBegin(await adapter.begin());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "error");
    } finally {
      setBusy(false);
    }
  }

  async function doDisable(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await adapter.disable(disableCode.trim());
      setDisabling(false);
      setDisableCode("");
      reload();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code.includes("invalid")
          ? t("twoFactor.invalidCode")
          : err instanceof ApiError
            ? err.message
            : t("twoFactor.invalidCode"),
      );
    } finally {
      setBusy(false);
    }
  }

  if (status === null) {
    return <div className="text-sm text-muted-foreground">{t("common.loading")}</div>;
  }

  // Enrollment in progress.
  if (begin) {
    return (
      <TotpEnroll
        begin={begin}
        onConfirm={adapter.confirm}
        onDone={() => {
          setBegin(null);
          reload();
        }}
      />
    );
  }

  if (status.enabled) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Badge tone="active">
            <ShieldCheck className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
            {t("twoFactor.enabled")}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {t("twoFactor.recoveryRemaining", {
              count: status.recovery_codes_remaining,
            })}
          </span>
        </div>
        {status.required ? (
          <p className="text-xs text-muted-foreground">{t("twoFactor.requiredNote")}</p>
        ) : disabling ? (
          <form onSubmit={doDisable} className="max-w-xs space-y-2">
            <Label htmlFor="disable-code" className="text-xs">
              {t("twoFactor.disableHelp")}
            </Label>
            <Input
              id="disable-code"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.trim())}
              placeholder="000000"
              className="font-mono tracking-widest"
              autoFocus
            />
            {error && <Alert tone="danger">{error}</Alert>}
            <div className="flex gap-2">
              <Button type="submit" variant="outline" disabled={busy || !disableCode}>
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />}
                {t("twoFactor.disable")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setDisabling(false);
                  setError(null);
                }}
              >
                {t("common.cancel")}
              </Button>
            </div>
          </form>
        ) : (
          <Button variant="outline" onClick={() => setDisabling(true)}>
            {t("twoFactor.disable")}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Badge tone="neutral">{t("twoFactor.disabled")}</Badge>
      </div>
      <p className="text-sm text-muted-foreground">{t("twoFactor.intro")}</p>
      {error && <Alert tone="danger">{error}</Alert>}
      <Button onClick={startEnroll} disabled={busy}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />}
        <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />
        {t("twoFactor.enable")}
      </Button>
    </div>
  );
}
